/**
 * ChannelForm Component
 *
 * Form for creating and editing channels with:
 * - Name input (slug format identifier)
 * - Display name input
 * - Description textarea
 * - Channel type selection (public, project, role_restricted)
 * - Project binding dropdown (for project type)
 * - Required roles multi-select (for role_restricted type)
 *
 * @module components/admin/ChannelForm
 */

import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import {
  Hash,
  FileText,
  Globe,
  FolderKanban,
  Shield,
  Save,
  X,
  Loader2,
  AlertCircle,
  Check,
  ChevronDown,
  Plus,
  Trash2
} from 'lucide-react';

// ============================================
// Constants
// ============================================

/**
 * Validation constraints matching backend Zod schemas
 */
const VALIDATION = {
  NAME: {
    MIN: 1,
    MAX: 64,
    PATTERN: /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/
  },
  DISPLAY_NAME: {
    MIN: 1,
    MAX: 128
  },
  DESCRIPTION: {
    MAX: 1024
  }
};

/**
 * Channel types matching backend ChannelTypeSchema
 */
const CHANNEL_TYPES = {
  PUBLIC: 'public',
  PROJECT: 'project',
  ROLE_RESTRICTED: 'role_restricted'
};

/**
 * Channel type options for the dropdown
 */
const CHANNEL_TYPE_OPTIONS = [
  {
    id: CHANNEL_TYPES.PUBLIC,
    name: 'Public',
    description: 'Visible to all users and agents',
    icon: Globe
  },
  {
    id: CHANNEL_TYPES.PROJECT,
    name: 'Project',
    description: 'Tied to a specific project',
    icon: FolderKanban
  },
  {
    id: CHANNEL_TYPES.ROLE_RESTRICTED,
    name: 'Role Restricted',
    description: 'Only accessible to users with specific roles',
    icon: Shield
  }
];

/**
 * Default form values matching backend CreateChannelSchema defaults
 */
const DEFAULT_FORM_VALUES = {
  name: '',
  displayName: '',
  description: '',
  type: CHANNEL_TYPES.PUBLIC,
  projectId: '',
  requiredRoles: []
};

// ============================================
// Validation Functions
// ============================================

/**
 * Validate channel name (slug format)
 * @param {string} value - Name to validate
 * @returns {string|null} Error message or null if valid
 */
function validateName(value) {
  if (!value || value.trim().length === 0) {
    return 'Name is required';
  }
  if (value.length > VALIDATION.NAME.MAX) {
    return `Name must be ${VALIDATION.NAME.MAX} characters or less`;
  }
  if (!VALIDATION.NAME.PATTERN.test(value)) {
    return 'Name must be lowercase alphanumeric with optional hyphens (not at start/end for multi-char names)';
  }
  return null;
}

/**
 * Validate display name
 * @param {string} value - Display name to validate
 * @returns {string|null} Error message or null if valid
 */
function validateDisplayName(value) {
  if (value && value.length > VALIDATION.DISPLAY_NAME.MAX) {
    return `Display name must be ${VALIDATION.DISPLAY_NAME.MAX} characters or less`;
  }
  return null;
}

/**
 * Validate description
 * @param {string} value - Description to validate
 * @returns {string|null} Error message or null if valid
 */
function validateDescription(value) {
  if (value && value.length > VALIDATION.DESCRIPTION.MAX) {
    return `Description must be ${VALIDATION.DESCRIPTION.MAX} characters or less`;
  }
  return null;
}

/**
 * Validate channel type
 * @param {string} value - Type to validate
 * @returns {string|null} Error message or null if valid
 */
function validateType(value) {
  if (!value) {
    return 'Channel type is required';
  }
  if (!Object.values(CHANNEL_TYPES).includes(value)) {
    return 'Invalid channel type';
  }
  return null;
}

/**
 * Validate project selection (required for project type)
 * @param {string} value - Project ID to validate
 * @param {string} channelType - Current channel type
 * @returns {string|null} Error message or null if valid
 */
function validateProjectId(value, channelType) {
  if (channelType === CHANNEL_TYPES.PROJECT && (!value || value.trim().length === 0)) {
    return 'Project is required for project channels';
  }
  return null;
}

/**
 * Validate required roles (required for role_restricted type)
 * @param {Array} value - Array of role identifiers
 * @param {string} channelType - Current channel type
 * @returns {string|null} Error message or null if valid
 */
function validateRequiredRoles(value, channelType) {
  if (channelType === CHANNEL_TYPES.ROLE_RESTRICTED && (!value || value.length === 0)) {
    return 'At least one role is required for role-restricted channels';
  }
  return null;
}

/**
 * Validate entire form
 * @param {object} values - Form values
 * @returns {object} Errors object with field keys
 */
function validateForm(values) {
  return {
    name: validateName(values.name),
    displayName: validateDisplayName(values.displayName),
    description: validateDescription(values.description),
    type: validateType(values.type),
    projectId: validateProjectId(values.projectId, values.type),
    requiredRoles: validateRequiredRoles(values.requiredRoles, values.type)
  };
}

/**
 * Check if form has any errors
 * @param {object} errors - Errors object
 * @returns {boolean} True if form has errors
 */
function hasErrors(errors) {
  return Object.values(errors).some((error) => error !== null);
}

// ============================================
// Sub-Components
// ============================================

/**
 * Form field wrapper with label and error display
 * @param {object} props - Component props
 * @param {string} props.id - Input ID
 * @param {string} props.label - Field label
 * @param {boolean} [props.required] - Whether field is required
 * @param {string} [props.error] - Error message
 * @param {string} [props.hint] - Hint text
 * @param {React.ReactNode} [props.icon] - Field icon
 * @param {React.ReactNode} props.children - Input element
 * @returns {React.ReactElement} Form field
 */
function FormField({ id, label, required, error, hint, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="flex items-center gap-2 text-sm font-medium text-gray-200"
      >
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Text input component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Input element
 */
function TextInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  hasError,
  disabled,
  type = 'text',
  maxLength
}) {
  return (
    <input
      type={type}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={`
        w-full px-3 py-2 rounded-md
        bg-gray-900 border text-gray-100 placeholder-gray-500
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${hasError
          ? 'border-red-500 focus:ring-red-500'
          : 'border-gray-700 hover:border-gray-600'
        }
      `}
    />
  );
}

/**
 * Textarea component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Textarea element
 */
function TextArea({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  hasError,
  disabled,
  rows = 3,
  maxLength
}) {
  const charCount = value?.length || 0;
  const showCharCount = maxLength && charCount > maxLength * 0.8;

  return (
    <div className="relative">
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        className={`
          w-full px-3 py-2 rounded-md resize-none
          bg-gray-900 border text-gray-100 placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${hasError
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-700 hover:border-gray-600'
          }
        `}
      />
      {showCharCount && (
        <span
          className={`absolute bottom-2 right-2 text-xs ${
            charCount >= maxLength ? 'text-red-400' : 'text-gray-500'
          }`}
        >
          {charCount}/{maxLength}
        </span>
      )}
    </div>
  );
}

/**
 * Select dropdown component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Select element
 */
function Select({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder = 'Select...',
  options = [],
  hasError,
  disabled,
  loading
}) {
  return (
    <div className="relative">
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 pr-10 rounded-md appearance-none
          bg-gray-900 border text-gray-100
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${hasError
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-700 hover:border-gray-600'
          }
          ${!value ? 'text-gray-500' : ''}
        `}
      >
        <option value="">{loading ? 'Loading...' : placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.displayName || option.name}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        {loading ? (
          <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </div>
    </div>
  );
}

/**
 * Channel type card component for visual selection
 * @param {object} props - Component props
 * @returns {React.ReactElement} Type card element
 */
const TypeCard = memo(function TypeCard({
  option,
  isSelected,
  onSelect,
  disabled
}) {
  const Icon = option.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      disabled={disabled}
      className={`
        flex items-start gap-3 p-3 rounded-lg border text-left
        transition-all duration-150
        ${isSelected
          ? 'border-indigo-500 bg-indigo-500/10 ring-1 ring-indigo-500'
          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <div
        className={`
          p-2 rounded-md
          ${isSelected ? 'bg-indigo-500/20' : 'bg-gray-700/50'}
        `}
      >
        <Icon
          className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : 'text-gray-400'}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm font-medium ${
            isSelected ? 'text-indigo-300' : 'text-gray-200'
          }`}
        >
          {option.name}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {option.description}
        </div>
      </div>
      {isSelected && (
        <Check className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
      )}
    </button>
  );
});

/**
 * Multi-select role picker component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Role picker element
 */
function RolePicker({
  id,
  selectedRoles,
  availableRoles,
  onAdd,
  onRemove,
  hasError,
  disabled,
  loading
}) {
  const [inputValue, setInputValue] = useState('');

  // Filter available roles that haven't been selected
  const unselectedRoles = useMemo(() => {
    return availableRoles.filter(
      (role) => !selectedRoles.includes(role.name)
    );
  }, [availableRoles, selectedRoles]);

  const handleAddRole = useCallback((roleName) => {
    if (roleName && !selectedRoles.includes(roleName)) {
      onAdd(roleName);
      setInputValue('');
    }
  }, [selectedRoles, onAdd]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddRole(inputValue.trim());
    }
  }, [inputValue, handleAddRole]);

  return (
    <div className="space-y-2">
      {/* Selected roles */}
      {selectedRoles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedRoles.map((roleName) => (
            <div
              key={roleName}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 border border-indigo-500/30"
            >
              <Shield className="w-3 h-3 text-indigo-400" />
              <span className="text-sm text-indigo-300">{roleName}</span>
              <button
                type="button"
                onClick={() => onRemove(roleName)}
                disabled={disabled}
                className="p-0.5 rounded hover:bg-indigo-500/30 text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add role input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <select
            id={id}
            value={inputValue}
            onChange={(e) => {
              if (e.target.value) {
                handleAddRole(e.target.value);
              }
            }}
            disabled={disabled || loading || unselectedRoles.length === 0}
            className={`
              w-full px-3 py-2 pr-10 rounded-md appearance-none
              bg-gray-900 border text-gray-100
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
              ${hasError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-700 hover:border-gray-600'
              }
            `}
          >
            <option value="">
              {loading
                ? 'Loading roles...'
                : unselectedRoles.length === 0
                  ? 'No more roles available'
                  : 'Select a role to add...'}
            </option>
            {unselectedRoles.map((role) => (
              <option key={role.id} value={role.name}>
                {role.displayName || role.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading ? (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Manual entry input */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.toLowerCase())}
          onKeyDown={handleKeyDown}
          placeholder="Or type role name..."
          disabled={disabled}
          className={`
            flex-1 px-3 py-2 rounded-md
            bg-gray-900 border text-gray-100 placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors border-gray-700 hover:border-gray-600
          `}
        />
        <button
          type="button"
          onClick={() => handleAddRole(inputValue.trim())}
          disabled={disabled || !inputValue.trim()}
          className="
            flex items-center gap-1 px-3 py-2 rounded-md
            text-gray-300 bg-gray-700 hover:bg-gray-600
            focus:outline-none focus:ring-2 focus:ring-gray-500
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          "
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {selectedRoles.length === 0 && (
        <p className="text-xs text-gray-500">
          No roles selected. Add roles that will have access to this channel.
        </p>
      )}
    </div>
  );
}

/**
 * Form action buttons
 * @param {object} props - Component props
 * @returns {React.ReactElement} Action buttons
 */
function FormActions({ onSubmit, onCancel, isSubmitting, isValid, submitLabel = 'Create Channel' }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
      <button
        type="button"
        onClick={onCancel}
        disabled={isSubmitting}
        className="
          flex items-center gap-2 px-4 py-2 rounded-md
          text-gray-300 bg-gray-700 hover:bg-gray-600
          focus:outline-none focus:ring-2 focus:ring-gray-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
      >
        <X className="w-4 h-4" />
        Cancel
      </button>
      <button
        type="submit"
        onClick={onSubmit}
        disabled={isSubmitting || !isValid}
        className="
          flex items-center gap-2 px-4 py-2 rounded-md
          text-white bg-indigo-600 hover:bg-indigo-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            {submitLabel}
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Success message component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Success message
 */
function SuccessMessage({ message, onDismiss }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-green-500/10 border border-green-500/30">
      <div className="flex items-center gap-2 text-green-400">
        <Check className="w-4 h-4" />
        <span className="text-sm">{message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded hover:bg-green-500/20 text-green-400"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Error message component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Error message
 */
function ErrorMessage({ message, onDismiss }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-red-500/10 border border-red-500/30">
      <div className="flex items-center gap-2 text-red-400">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">{message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded hover:bg-red-500/20 text-red-400"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Form section header component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Section header
 */
function SectionHeader({ title, description }) {
  return (
    <div className="pb-2 mb-4 border-b border-gray-700/50">
      <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
      {description && (
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * ChannelForm Component
 *
 * Form for creating and editing channels with validation and submission handling.
 *
 * @param {object} props - Component props
 * @param {Array} [props.projects] - Available projects for selection
 * @param {boolean} [props.projectsLoading] - Whether projects are loading
 * @param {Array} [props.roles] - Available roles for selection
 * @param {boolean} [props.rolesLoading] - Whether roles are loading
 * @param {object} [props.initialValues] - Initial form values for editing
 * @param {Function} props.onSubmit - Form submission handler (receives form values)
 * @param {Function} [props.onCancel] - Cancel button handler
 * @param {Function} [props.onSuccess] - Success callback
 * @param {string} [props.submitLabel] - Custom submit button label
 * @param {boolean} [props.isEditing] - Whether form is in edit mode
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Channel form
 */
export default function ChannelForm({
  projects = [],
  projectsLoading = false,
  roles = [],
  rolesLoading = false,
  initialValues,
  onSubmit,
  onCancel,
  onSuccess,
  submitLabel,
  isEditing = false,
  className = ''
}) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  const [values, setValues] = useState(() => ({
    ...DEFAULT_FORM_VALUES,
    ...initialValues
  }));
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  /**
   * Check if form is valid (has values and no errors)
   */
  const isFormValid = useMemo(() => {
    const formErrors = validateForm(values);
    return !hasErrors(formErrors);
  }, [values]);

  /**
   * Generate default display name from name
   */
  const suggestedDisplayName = useMemo(() => {
    if (!values.name) return '';
    return values.name
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [values.name]);

  /**
   * Check if project selection should be shown
   */
  const showProjectSelect = values.type === CHANNEL_TYPES.PROJECT;

  /**
   * Check if roles selection should be shown
   */
  const showRolesSelect = values.type === CHANNEL_TYPES.ROLE_RESTRICTED;

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  /**
   * Handle input change
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSubmitSuccess(null);
  }, []);

  /**
   * Handle type change (from type cards)
   */
  const handleTypeChange = useCallback((newType) => {
    setValues((prev) => ({
      ...prev,
      type: newType,
      // Clear conditional fields when type changes
      projectId: newType === CHANNEL_TYPES.PROJECT ? prev.projectId : '',
      requiredRoles: newType === CHANNEL_TYPES.ROLE_RESTRICTED ? prev.requiredRoles : []
    }));
    setSubmitError(null);
    setSubmitSuccess(null);
  }, []);

  /**
   * Handle adding a role
   */
  const handleAddRole = useCallback((roleName) => {
    setValues((prev) => ({
      ...prev,
      requiredRoles: [...prev.requiredRoles, roleName]
    }));
    setSubmitError(null);
    setSubmitSuccess(null);
  }, []);

  /**
   * Handle removing a role
   */
  const handleRemoveRole = useCallback((roleName) => {
    setValues((prev) => ({
      ...prev,
      requiredRoles: prev.requiredRoles.filter((r) => r !== roleName)
    }));
    setSubmitError(null);
    setSubmitSuccess(null);
  }, []);

  /**
   * Handle input blur (mark as touched)
   */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    const fieldValidators = {
      name: () => validateName(values.name),
      displayName: () => validateDisplayName(values.displayName),
      description: () => validateDescription(values.description),
      type: () => validateType(values.type),
      projectId: () => validateProjectId(values.projectId, values.type),
      requiredRoles: () => validateRequiredRoles(values.requiredRoles, values.type)
    };

    const validator = fieldValidators[name];
    if (validator) {
      setErrors((prev) => ({
        ...prev,
        [name]: validator()
      }));
    }
  }, [values]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();

      // Validate all fields
      const formErrors = validateForm(values);
      setErrors(formErrors);
      setTouched({
        name: true,
        displayName: true,
        description: true,
        type: true,
        projectId: true,
        requiredRoles: true
      });

      if (hasErrors(formErrors)) {
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(null);

      try {
        // Clean up values before submission
        const submissionValues = {
          name: values.name.trim(),
          type: values.type,
          ...(values.displayName?.trim() && { displayName: values.displayName.trim() }),
          ...(values.description?.trim() && { description: values.description.trim() }),
          ...(values.type === CHANNEL_TYPES.PROJECT && values.projectId && {
            projectId: values.projectId
          }),
          ...(values.type === CHANNEL_TYPES.ROLE_RESTRICTED && values.requiredRoles.length > 0 && {
            requiredRoles: values.requiredRoles
          })
        };

        await onSubmit(submissionValues);

        setSubmitSuccess(
          isEditing
            ? 'Channel updated successfully'
            : 'Channel created successfully'
        );

        // Reset form if creating (not editing)
        if (!isEditing) {
          setValues(DEFAULT_FORM_VALUES);
          setTouched({});
          setErrors({});
        }

        onSuccess?.();
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Failed to save channel. Please try again.';
        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, onSubmit, onSuccess, isEditing]
  );

  /**
   * Handle cancel button
   */
  const handleCancel = useCallback(() => {
    setValues({ ...DEFAULT_FORM_VALUES, ...initialValues });
    setTouched({});
    setErrors({});
    setSubmitError(null);
    setSubmitSuccess(null);
    onCancel?.();
  }, [initialValues, onCancel]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  // Update form when initialValues change (for editing)
  useEffect(() => {
    if (initialValues) {
      setValues((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
    >
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-700">
        <div className="p-2 rounded-lg bg-cyan-500/10">
          <Hash className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit Channel' : 'Create Channel'}
          </h2>
          <p className="text-sm text-gray-400">
            {isEditing
              ? 'Update the channel configuration'
              : 'Create a new channel for messaging'}
          </p>
        </div>
      </div>

      {/* Status Messages */}
      {submitSuccess && (
        <SuccessMessage
          message={submitSuccess}
          onDismiss={() => setSubmitSuccess(null)}
        />
      )}
      {submitError && (
        <ErrorMessage
          message={submitError}
          onDismiss={() => setSubmitError(null)}
        />
      )}

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Basic Information Section */}
        <div>
          <SectionHeader
            title="Basic Information"
            description="Channel identification and description"
          />
          <div className="space-y-4">
            {/* Name */}
            <FormField
              id="name"
              label="Name"
              required
              icon={Hash}
              error={touched.name ? errors.name : null}
              hint="Unique identifier (lowercase alphanumeric with hyphens)"
            >
              <TextInput
                id="name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., general, dev-team, announcements"
                hasError={touched.name && errors.name}
                disabled={isSubmitting || isEditing}
                maxLength={VALIDATION.NAME.MAX}
              />
            </FormField>

            {/* Display Name */}
            <FormField
              id="displayName"
              label="Display Name"
              icon={FileText}
              error={touched.displayName ? errors.displayName : null}
              hint={
                !values.displayName && suggestedDisplayName
                  ? `Suggested: "${suggestedDisplayName}"`
                  : 'Human-readable name shown in the UI'
              }
            >
              <TextInput
                id="displayName"
                name="displayName"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={suggestedDisplayName || 'e.g., General, Dev Team'}
                hasError={touched.displayName && errors.displayName}
                disabled={isSubmitting}
                maxLength={VALIDATION.DISPLAY_NAME.MAX}
              />
            </FormField>

            {/* Description */}
            <FormField
              id="description"
              label="Description"
              icon={FileText}
              error={touched.description ? errors.description : null}
              hint="Brief description of the channel's purpose"
            >
              <TextArea
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., General discussion and announcements"
                hasError={touched.description && errors.description}
                disabled={isSubmitting}
                rows={2}
                maxLength={VALIDATION.DESCRIPTION.MAX}
              />
            </FormField>
          </div>
        </div>

        {/* Channel Type Section */}
        <div>
          <SectionHeader
            title="Channel Type"
            description="Control who can access this channel"
          />
          <div className="space-y-4">
            {/* Type Selection Cards */}
            <div className="grid grid-cols-1 gap-3">
              {CHANNEL_TYPE_OPTIONS.map((option) => (
                <TypeCard
                  key={option.id}
                  option={option}
                  isSelected={values.type === option.id}
                  onSelect={handleTypeChange}
                  disabled={isSubmitting || isEditing}
                />
              ))}
            </div>
            {touched.type && errors.type && (
              <p className="flex items-center gap-1 text-xs text-red-400">
                <AlertCircle className="w-3 h-3" />
                {errors.type}
              </p>
            )}
          </div>
        </div>

        {/* Project Selection (conditional) */}
        {showProjectSelect && (
          <div>
            <SectionHeader
              title="Project Binding"
              description="Link this channel to a project"
            />
            <FormField
              id="projectId"
              label="Project"
              required
              icon={FolderKanban}
              error={touched.projectId ? errors.projectId : null}
              hint="Select the project this channel belongs to"
            >
              <Select
                id="projectId"
                name="projectId"
                value={values.projectId}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Select a project..."
                options={projects}
                hasError={touched.projectId && errors.projectId}
                disabled={isSubmitting}
                loading={projectsLoading}
              />
            </FormField>
          </div>
        )}

        {/* Role Selection (conditional) */}
        {showRolesSelect && (
          <div>
            <SectionHeader
              title="Required Roles"
              description="Specify which roles can access this channel"
            />
            <FormField
              id="requiredRoles"
              label="Roles"
              required
              icon={Shield}
              error={touched.requiredRoles ? errors.requiredRoles : null}
              hint="Users must have at least one of these roles to access the channel"
            >
              <RolePicker
                id="requiredRoles"
                selectedRoles={values.requiredRoles}
                availableRoles={roles}
                onAdd={handleAddRole}
                onRemove={handleRemoveRole}
                hasError={touched.requiredRoles && errors.requiredRoles}
                disabled={isSubmitting}
                loading={rolesLoading}
              />
            </FormField>
          </div>
        )}
      </div>

      {/* Actions */}
      <FormActions
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        isValid={isFormValid}
        submitLabel={submitLabel || (isEditing ? 'Save Changes' : 'Create Channel')}
      />
    </form>
  );
}

// ============================================
// Named Exports for Sub-Components
// ============================================

export {
  FormField,
  TextInput,
  TextArea,
  Select,
  TypeCard,
  RolePicker,
  FormActions,
  SuccessMessage,
  ErrorMessage,
  SectionHeader,
  validateName,
  validateDisplayName,
  validateDescription,
  validateType,
  validateProjectId,
  validateRequiredRoles,
  validateForm,
  hasErrors,
  VALIDATION,
  CHANNEL_TYPES,
  CHANNEL_TYPE_OPTIONS,
  DEFAULT_FORM_VALUES
};
